import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { FileSpreadsheet, LogIn } from "lucide-react";
import LoginBrandVideo from "@/components/LoginBrandVideo";
import IntroDemoSwitcher from "@/components/IntroDemoSwitcher";
import FullscreenIntroVideo, { SharpIntroVideo } from "@/components/FullscreenIntroVideo";
import { getIntroTheme } from "@/lib/seasonalIntro";

const introTheme = getIntroTheme();

type QuickAccount = { email: string; storageKey: string; shortcut: string; alt?: boolean };
const QUICK_ACCOUNTS: QuickAccount[] = [
  { email: "silvia.sanchez@ferreiroyportelo.es", storageKey: "lovable_quick_login_silvia", shortcut: "p", alt: false },
  { email: "joaquin@ferreiroyportelo.es", storageKey: "lovable_quick_login_joaquin", shortcut: "j", alt: true },
];
const QUICK_LOGIN_XOR = "fp-quick-login-2026";

const obfuscate = (text: string) => {
  const out: number[] = [];
  for (let i = 0; i < text.length; i++) {
    out.push(text.charCodeAt(i) ^ QUICK_LOGIN_XOR.charCodeAt(i % QUICK_LOGIN_XOR.length));
  }
  return btoa(String.fromCharCode(...out));
};

const deobfuscate = (encoded: string): string | null => {
  try {
    const raw = atob(encoded);
    const out: string[] = [];
    for (let i = 0; i < raw.length; i++) {
      out.push(String.fromCharCode(raw.charCodeAt(i) ^ QUICK_LOGIN_XOR.charCodeAt(i % QUICK_LOGIN_XOR.length)));
    }
    return out.join("");
  } catch {
    return null;
  }
};

const Login = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const { user } = useAuth();
  const [hasQuickLogin, setHasQuickLogin] = useState<Record<string, boolean>>({});
  const [quickDialogOpen, setQuickDialogOpen] = useState(false);
  const [quickAccount, setQuickAccount] = useState<QuickAccount | null>(null);
  const [quickPassword, setQuickPassword] = useState("");
  const quickLoginInFlight = useRef(false);

  useEffect(() => {
    if (user) {
      navigate("/hub", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const map: Record<string, boolean> = {};
    for (const acc of QUICK_ACCOUNTS) {
      map[acc.storageKey] = !!localStorage.getItem(acc.storageKey);
    }
    setHasQuickLogin(map);
  }, []);

  const runQuickLogin = async (account: QuickAccount, pwd: string) => {
    if (quickLoginInFlight.current) return;
    quickLoginInFlight.current = true;
    setEmail(account.email);
    setPassword(pwd);
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: account.email,
      password: pwd,
    });
    setLoading(false);
    quickLoginInFlight.current = false;

    if (error) {
      localStorage.removeItem(account.storageKey);
      setHasQuickLogin((p) => ({ ...p, [account.storageKey]: false }));
      toast({
        title: "Acceso rápido no válido",
        description: "La contraseña guardada ya no funciona. Vuelve a configurarla con el atajo de teclado.",
        variant: "destructive",
      });
      return;
    }
    navigate("/hub", { replace: true });
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      const account = QUICK_ACCOUNTS.find((a) => a.shortcut === key);
      if (!account) return;
      const ctrlPressed = e.ctrlKey || e.metaKey;
      if (!account.alt && !ctrlPressed) return;
      if (account.alt && !e.altKey) return;
      if (!account.alt && e.altKey) return;
      e.preventDefault();
      const stored = localStorage.getItem(account.storageKey);
      const pwd = stored ? deobfuscate(stored) : null;
      if (pwd) {
        runQuickLogin(account, pwd);
      } else {
        setQuickAccount(account);
        setQuickPassword("");
        setQuickDialogOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleQuickSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickPassword || !quickAccount) return;
    const account = quickAccount;
    localStorage.setItem(account.storageKey, obfuscate(quickPassword));
    setHasQuickLogin((p) => ({ ...p, [account.storageKey]: true }));
    setQuickDialogOpen(false);
    const pwd = quickPassword;
    setQuickPassword("");
    await runQuickLogin(account, pwd);
  };

  const handleForgetQuickLogin = (account: QuickAccount) => {
    localStorage.removeItem(account.storageKey);
    setHasQuickLogin((p) => ({ ...p, [account.storageKey]: false }));
    toast({ title: "Acceso rápido eliminado", description: `Se ha borrado el acceso rápido de ${account.email}.` });
  };




  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);

    if (error) {
      const title =
        error.message === "Invalid login credentials"
          ? "No se puede iniciar sesión"
          : "Error de inicio de sesión";
      const description =
        error.message === "Invalid login credentials"
          ? "Tu cuenta no existe o la contraseña es incorrecta. Si tu acceso ha sido revocado por el administrador, contacta con él. Si solo olvidaste la contraseña, pulsa «¿Olvidaste tu contraseña?»."
          : error.message;

      toast({ title, description, variant: "destructive" });
      return;
    }

    navigate("/hub", { replace: true });
  };




  const handleForgotPassword = async () => {
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      toast({
        title: "Indica tu email",
        description: "Escribe primero tu email para solicitar el reseteo al administrador.",
        variant: "destructive",
      });
      return;
    }

    setResetLoading(true);
    const { error } = await supabase
      .from("password_reset_requests")
      .insert({ email: cleanEmail } as any);
    setResetLoading(false);

    if (error) {
      toast({ title: "No se pudo enviar la solicitud", description: error.message, variant: "destructive" });
      return;
    }

    toast({
      title: "Solicitud enviada al administrador",
      description: "Recibirás una nueva contraseña provisional cuando el administrador la apruebe. Ponte en contacto con él para acelerar el proceso.",
    });
  };


  return (
    <div
      className={`min-h-screen flex flex-col px-6 py-8 text-foreground ${introTheme.dark ? "dark" : ""}`}
      style={{ background: introTheme.background }}
    >
      {introTheme.fullscreen && <FullscreenIntroVideo src={introTheme.video} />}
      <IntroDemoSwitcher />
      <div className={`flex-1 flex flex-col items-center justify-center w-full ${introTheme.fullscreen ? "relative z-10" : ""}`}>
      {introTheme.fullscreen && <SharpIntroVideo src={introTheme.video} />}
      <div className={`flex flex-col items-center max-w-sm w-full text-center space-y-6 ${introTheme.fullscreen ? "mt-4 rounded-2xl border border-border/40 bg-background/60 backdrop-blur-md p-6 shadow-2xl" : ""}`}>
        {!introTheme.fullscreen && <LoginBrandVideo />}

        <div className="flex items-center gap-2">
          <FileSpreadsheet className="h-6 w-6 text-[hsl(var(--brand-green))]" />
          <p className="text-base font-medium text-muted-foreground tracking-wide">Procesador de facturas</p>
        </div>

        <form onSubmit={handleLogin} className="w-full space-y-4 text-left" autoComplete="off">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              required
              autoComplete="off"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="password">Contraseña</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              minLength={6}
              autoComplete="new-password"
            />
          </div>

          <button
            type="button"
            onClick={handleForgotPassword}
            disabled={loading || resetLoading}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {resetLoading ? "Enviando solicitud…" : "¿Olvidaste tu contraseña? Solicitar al administrador"}
          </button>

          <Button
            type="submit"
            disabled={loading || resetLoading}
            className="w-full bg-[hsl(var(--brand-green))] hover:bg-[hsl(var(--brand-green))]/90 text-white font-semibold gap-2"
          >
            <LogIn className="h-4 w-4" />
            {loading ? "Cargando…" : "Iniciar sesión"}
          </Button>
        </form>


        <div className="flex flex-col items-center gap-1 text-xs text-muted-foreground/70 text-center">
          <p>Acceso restringido. Solicita tu cuenta al administrador.</p>
          {QUICK_ACCOUNTS.filter((a) => hasQuickLogin[a.storageKey]).map((a) => (
            <button
              key={a.storageKey}
              type="button"
              onClick={() => handleForgetQuickLogin(a)}
              className="text-muted-foreground/60 hover:text-foreground transition-colors underline underline-offset-2"
            >
              Olvidar acceso rápido de {a.email}
            </button>
          ))}
        </div>
      </div>

      <Dialog open={quickDialogOpen} onOpenChange={setQuickDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Configurar acceso rápido</DialogTitle>
            <DialogDescription>
              Introduce la contraseña de <strong>{quickAccount?.email}</strong> una sola vez. Se guardará ofuscada en este navegador para entrar con el atajo de teclado.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleQuickSave} className="space-y-3" autoComplete="off">
            <div className="space-y-1.5">
              <Label htmlFor="quick-password">Contraseña</Label>
              <Input
                id="quick-password"
                type="password"
                value={quickPassword}
                onChange={(e) => setQuickPassword(e.target.value)}
                placeholder="••••••••"
                autoFocus
                autoComplete="new-password"
                required
                minLength={6}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setQuickDialogOpen(false)}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={!quickPassword || loading}
                className="bg-[hsl(var(--brand-green))] hover:bg-[hsl(var(--brand-green))]/90 text-white"
              >
                Guardar y entrar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      </div>

      <footer className="mt-8 text-muted-foreground/50 text-xs flex items-center justify-center gap-3">
        <span>© {new Date().getFullYear()} Ferreiro & Portelo</span>
        <span>·</span>
        <button onClick={() => navigate("/privacidad")} className="hover:text-muted-foreground transition-colors">
          Política de privacidad
        </button>
      </footer>
    </div>
  );
};

export default Login;
